import React, { SyntheticEvent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, TableCell } from '@material-ui/core';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import useStyles from './CellWithFilter.styles';
import { useCallbackOne, useMemoOne } from 'use-memo-one';
import { TableStateContext } from '../../../contexts/tableState/TableStateContext';
import { endpoints } from '../../../utils/endpoints';
import useApi from '../../../utils/useApi';
import { getId } from '../../../utils/idForListItem';
import {
  createInitFilterState,
  IFilter,
  IFiltering,
  IListItem,
  useFiltersContext,
} from '../../../contexts/filter/FiltersProvider';
import _ from 'lodash';
import ArrowIcon from '@material-ui/icons/ArrowDropDown';
import { ETables } from '../../../types/enums/ETables';
import Filter from './components/Filter';
import Item from './components/Item/Item';
import {
  calcPosition,
  columnKeysWithUnderscore,
  getAllFilteringFromTable,
  getCurrentState,
  getSelectedFiltersAsFiltering,
  isTableListPage,
  resetItemListScroll,
  shouldHasDateInput,
} from '../../../utils/cellWithFilter';
import { useHistory } from 'react-router-dom';
export interface IPosition {
  left: number;
  top: number;
}

interface IProps {
  tableColumn: any;
  tableName: string;
}
const CellWithFilter: React.FC<IProps> = ({ tableName, tableColumn }) => {
  const styles = useStyles();
  const history = useHistory();
  const { get, isFetching } = useApi(null);
  const { setFiltering, setSorting, state: tableState, setPage: setTablePage } = useContext(TableStateContext);

  const cellRef = useRef<HTMLDivElement>(null);
  const [componentPosition, setComponentPosition] = useState<IPosition>({ left: 0, top: 0 });

  const {
    initFilterState,
    filters,
    toggleIsOpen,
    increasePage,
    setItems,
    setSearch,
    setSelectedItems,
    setIsUsing,
    removeSelectedFilter,
    resetPage,
    toggleSorting,
    setHasMoreItems,
    toggleAllItemsChecking,
  } = useFiltersContext();

  const isOnTablePage: boolean = useMemoOne(() => isTableListPage(history.location.pathname, tableName), [
    history.location.pathname,
    tableName,
  ]);

  const filter: IFilter = useMemoOne(
    () => getCurrentState(filters, tableName, tableColumn.id) || createInitFilterState(tableColumn.id),
    [tableName, tableColumn.id, filters, isOnTablePage],
  );

  const searchInputType: string = useMemoOne(
    () => (shouldHasDateInput(tableColumn.id) ? 'dateInputs' : 'textField'),
    [],
  );

  const handleCheckItem = useCallbackOne(
    (label: string) => {
      const item: IListItem | undefined = filter.items.find((item) => item.label === label);

      if (item) item.checked = !item.checked;

      setItems(tableName, tableColumn.id, false, [...filter.items]);

      const checkedItems = filter.items
        .filter((item) => item.checked)
        .reduce((acc: string[], item: IListItem) => [...acc, item.label], []);

      setSelectedItems(tableName, tableColumn.id, checkedItems);
    },
    [filter.items, setItems, tableName, tableColumn.id],
  );

  const jsxListItems = useMemoOne(
    () =>
      filter.items.length
        ? filter.items.map((item) => <Item key={getId()} toggleItem={handleCheckItem} item={item} styles={styles} />)
        : [],
    [filter.items],
  );

  useEffect(() => {
    initFilterState(tableName, tableColumn.id);
  }, []);

  useEffect(() => {
    if (cellRef) setComponentPosition(calcPosition(cellRef));
  }, [cellRef]);

  const fetchItems = useCallbackOne(() => {
    const fetchDate = shouldHasDateInput(tableColumn.id);

    const filtering = getSelectedFiltersAsFiltering(filter.selectedFilters);

    const filterItems: any[] = [...filtering];

    if (!fetchDate) filterItems.push({ key: '_searchable', value: `${tableColumn.id}:${filter.search}` });
    else {
      filterItems.push(
        { key: tableColumn.id + 'From', value: new Date(filter.dateInput.from) },
        { key: tableColumn.id + 'To', value: new Date(filter.dateInput.to) },
      );
    }
    get({
      endpoint: endpoints.tableFilter({
        tableName,
        page: filter.page,
        filter: filterItems,
      }),

      onSuccess: ({ data }) => {
        const hasMoreItems: boolean = !!data.length;

        setHasMoreItems(tableName, tableColumn.id, hasMoreItems);

        const isNextPageFetched = filter.page !== 1;

        if (!isNextPageFetched) resetItemListScroll();

        const formattedItems: IListItem[] = data.map((label: string) => ({
          label,
          checked: filter.allItemsIsChecked,
        }));

        setItems(tableName, tableColumn.id, isNextPageFetched, formattedItems);
      },
    });
  }, [
    tableName,
    tableColumn.id,
    filter.search,
    filter.page,
    // filter.selectedFilters,
    filter.dateInput,
    setItems,
    setHasMoreItems,
  ]);

  useEffect(() => {
    if (isOnTablePage) fetchItems();
  }, [fetchItems, isOnTablePage]);

  const toggleVisibility = useCallbackOne(() => {
    toggleIsOpen(tableName, tableColumn.id);
  }, [tableName, tableColumn.id, toggleIsOpen]);

  const handleOnClickAway = useCallbackOne(
    (event: Event) => {
      const clickedColumnId: string | undefined = (event?.target as HTMLDivElement).dataset.column_id;

      if (!clickedColumnId) return toggleVisibility();
    },

    [toggleVisibility, tableName, tableColumn],
  );

  const handleToggleAll = useCallbackOne(() => {
    toggleAllItemsChecking(tableName, tableColumn.id);
  }, [tableName, tableColumn.id, filters, filter]);

  const handleOnInputSearch = useCallbackOne(
    (event) => {
      const formated = event.target.value;

      resetPage(tableName, tableColumn.id);

      setSearch(tableName, tableColumn.id, formated);
    },
    [tableName, tableColumn.id, filter],
  );

  const handleToggleSorting = useCallbackOne(() => {
    toggleSorting(tableName, tableColumn.id);
  }, [tableName, tableColumn.id, filter]);

  const onListScrollingEnd = useCallbackOne(
    _.throttle(() => increasePage(tableName, tableColumn.id), 100),
    [tableName, tableColumn.id],
  );

  const handleOnResetSearch = useCallbackOne(() => {
    setSearch(tableName, tableColumn.id, '');
    resetPage(tableName, tableColumn.id);
    resetItemListScroll();
  }, [tableName, tableColumn.id]);

  const handleOnClearFilter = useCallbackOne(
    (event: SyntheticEvent) => {
      event.stopPropagation();

      const currentTableStateFiltering = tableState[tableName]?.filtering || [];

      const clearedFiltering = currentTableStateFiltering.filter(
        (item: IFiltering) => item.key !== columnKeysWithUnderscore(tableColumn.id),
      );

      setFiltering(tableName, clearedFiltering);

      setIsUsing(tableName, tableColumn.id, false);

      setSorting(tableName, { accessor: 'status', asc: tableName === ETables.PromoCodes ? false : true });

      removeSelectedFilter(tableName, tableColumn.id);
    },
    [tableName, tableColumn, setSorting, removeSelectedFilter, tableState, filters],
  );

  const handleOnClickFilterButton = useCallbackOne(() => {
    setIsUsing(tableName, tableColumn.id, true);

    toggleVisibility();

    const filtering = getAllFilteringFromTable(filters, tableName).map((filtering) => {
      filtering.key = columnKeysWithUnderscore(filtering.key);

      return filtering;
    });
    // console.log('filtering', filtering);

    setFiltering(tableName, filtering);

    setSorting(tableName, filter.sorting);

    setTablePage(tableName, 1);
  }, [tableName, tableColumn, filter]);

  return (
    <TableCell
      {...tableColumn.getHeaderProps()}
      data-column_id={tableColumn.id}
      className={styles.headerCell}
      ref={cellRef}
    >
      <CellViewContent
        filterIsUsing={filter.isUsing}
        tableColumn={tableColumn}
        toggleVisibility={toggleVisibility}
        handleOnClearFilter={handleOnClearFilter}
        styles={styles}
      />

      <Filter.Container
        isOpen={filter.isOpen}
        position={componentPosition}
        onClickAway={handleOnClickAway}
        onAnimationEnd={() => ''}
        styles={styles}
      >
        <Filter.AllItemsToggler handleToggle={handleToggleAll} styles={styles} />

        <Filter.SearchInput
          styles={styles}
          clearInput={handleOnResetSearch}
          inputType={searchInputType}
          input={filter.search}
          handleOnChange={handleOnInputSearch}
          tableName={tableName}
          column={tableColumn.id}
          filter={filter}
        />

        <Filter.ItemsList
          isFetching={isFetching}
          jsxListItems={jsxListItems}
          isAvaibleMoreItems={filter.hasMoreItems}
          onEndScrolling={onListScrollingEnd}
          styles={styles}
        />

        <Filter.Footer
          handleClickSorting={handleToggleSorting}
          sortingAsc={filter.sorting.asc}
          onClickFilterButton={handleOnClickFilterButton}
          styles={styles}
        />
      </Filter.Container>
    </TableCell>
  );
};

interface ICellViewContent {
  filterIsUsing: boolean;
  styles: Record<string, any>;
  tableColumn: any;
  toggleVisibility: () => void;
  handleOnClearFilter: (event: SyntheticEvent) => void;
}

const CellViewContent: React.FC<ICellViewContent> = ({
  filterIsUsing,
  styles,
  tableColumn,
  toggleVisibility,
  handleOnClearFilter,
}) => (
  <div className={styles.cellContent} data-column_id={tableColumn.id} onClick={toggleVisibility}>
    {filterIsUsing && <HighlightOffIcon className={styles.removeFilterIcon} onClick={handleOnClearFilter} />}

    <Box className={styles.withoutEvents}>{tableColumn.render('Header')}</Box>

    <ArrowIcon className={styles.withoutEvents} />
  </div>
);

export default CellWithFilter;
